import {z} from 'zod';
import {
  agentServiceToolDisplayConfig,
  toolsMap,
  type ClientFrameworkEvent,
} from '@packages/domains/ai-assistant/data-types';

const argsWithIntent = z.object({intent: z.string().min(1)});

const toolEventSchema = z.object({
  tool: z.string(),
  input: z.unknown().optional(),
  args: z.unknown().optional(),
  status: z.string().optional(),
  isComplete: z.boolean().optional(),
});

// Without this the gap after a write shows only "Thinking".
const POST_EXECUTION_LABELS: Record<string, string | undefined> = {
  'wfs-write': 'Applying changes',
  'wfs-edit': 'Applying changes',
};

export function getThinkingLabel(
  event: ClientFrameworkEvent | undefined
): string | undefined {
  if (!event) return undefined;

  const parsed = toolEventSchema.safeParse(event);
  if (!parsed.success) return undefined;

  const {tool: toolName, status, isComplete} = parsed.data;
  // Tool events carry args as `input` (ToolStatusEvent) or `args` (ToolPartialEvent).
  const rawArgs = parsed.data.input ?? parsed.data.args;

  // Completed tools normally fall back to the generic spinner — the previous
  // tool's intent would be misleading once the model is deciding what's next.
  // Exception: WFS writes bridge the gap with an explicit label.
  if (status === 'executed') {
    return POST_EXECUTION_LABELS[toolName];
  }

  // A terminal status already shows a finished chip; rendering the tool's
  // in-progress label (intent or prettyStatus.executing) under it would
  // contradict the chip.
  if (status === 'failed' || status === 'cancelled' || status === 'partial') {
    return undefined;
  }

  // On a tool_partial this means args finished streaming, not that the tool ran.
  if (isComplete === true) return undefined;

  const intent = argsWithIntent.safeParse(rawArgs);
  if (intent.success) return intent.data.intent;

  // ask-user-question's executing label is "Waiting for your answer" — that's
  // accurate while the user is choosing, but misleading once they've answered
  // and we're back to the model deciding next steps. Fall through to the
  // ThinkingIndicator's generic default in that case.
  if (toolName === 'ask-user-question') return undefined;

  const agentDisplay = agentServiceToolDisplayConfig[toolName];
  if (agentDisplay) {
    const formatted =
      agentDisplay.formatStatus?.('executing', rawArgs) ??
      agentDisplay.prettyName;
    // ThinkingIndicator renders the label as raw text, unlike the tool chips.
    return formatted.replace(/`/g, '');
  }

  const manifest = toolsMap[toolName as keyof typeof toolsMap];
  return manifest?.prettyStatus?.executing ?? manifest?.prettyName;
}
